import { ApPayee } from '@/components/ApPayee';
import { MAX_VALUE_AMOUNT, MIN_VALUE_AMOUNT } from '@/components/constants';
import { DeptSelect } from '@/components/DeptSelect';
import { KeywordCont } from '@/components/KeywordCont';
import { OfficeMember } from '@/components/OfficeMember';
import type { AdminInvoiceReportsSearchFormData } from '@/features/admin_invoice_reports/types';
import type { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';

import {
  ButtonV2,
  Form,
  FormDatepicker,
  FormInput,
  FormInputNumber,
  FormSelect,
  Label,
  type DatepickerProps,
  type InputNumberProps,
  type SelectProps,
} from '@moneyforward/ap-frontend-components';
import { Col, Row } from 'antd';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { useConfigForm } from '../../hooks/useConfigForm';
import styles from '../../Search.module.scss';
import { PaymentStatusSelect } from '@/components/PaymentStatusSelect';

const cx = classnames.bind(styles);

const ROW_GUTTER: [number, number] = [12, 12];

const EMPTY_AP_PAYEE_OPTIONS: never[] = [];

interface SearchFormProps {
  form: UseFormReturn<AdminInvoiceReportsSearchFormData>;
  onSubmit: (data: AdminInvoiceReportsSearchFormData) => void;
  onReset: () => void;
  isLoading?: boolean;
  labels: ReturnType<typeof useConfigForm>['labels'];
  selectProps: ReturnType<typeof useConfigForm>['selectProps'];
  inputProps: ReturnType<typeof useConfigForm>['inputProps'];
  statusOptions: ReturnType<typeof useConfigForm>['statusOptions'];
  paymentStatusOptions: ReturnType<
    typeof useConfigForm
  >['paymentStatusOptions'];
  mfFileOptions: ReturnType<typeof useConfigForm>['mfFileOptions'];
  paymentMethodOptions: ReturnType<
    typeof useConfigForm
  >['paymentMethodOptions'];
  deptsExisted?: ReturnType<typeof useConfigForm>['deptsExisted'];
  reportDeptsSetting?: ReturnType<typeof useConfigForm>['reportDeptsSetting'];
  memberOptions: BaseOption[];
  applicantDeptOptions: BaseOption[];
  reportDeptOptions: BaseOption[];
}

export const SearchForm: FC<SearchFormProps> = memo(
  ({
    form,
    onSubmit,
    onReset,
    isLoading = false,
    labels,
    selectProps,
    inputProps,
    statusOptions,
    paymentStatusOptions,
    mfFileOptions,
    paymentMethodOptions,
    deptsExisted,
    reportDeptsSetting,
    memberOptions,
    applicantDeptOptions,
    reportDeptOptions,
  }) => {
    const { control } = form;
    const { t } = useTranslation();

    // Date picker shared props
    const datepickerProps: DatepickerProps = useMemo(
      () => ({
        placeholder: [t('date_gteq_placeholder'), t('date_lteq_placeholder')],
        size: 'small',
      }),
      [t]
    );

    // Amount number input shared props
    const amountInputProps: InputNumberProps = useMemo(
      () => ({
        format: 'price_with_empty',
        max: MAX_VALUE_AMOUNT,
        min: MIN_VALUE_AMOUNT,
        stringMode: true,
        size: 'small',
      }),
      []
    );

    // Status select props
    const statusSelectProps: SelectProps = useMemo(
      () => ({
        options: statusOptions,
        isAllowClear: true,
        size: 'small',
      }),
      [statusOptions]
    );

    // mf_file select props
    const mfFileSelectProps: SelectProps = useMemo(
      () => ({
        options: mfFileOptions,
        isAllowClear: true,
        size: 'small',
      }),
      [mfFileOptions]
    );

    // Payment method select props
    const paymentMethodSelectProps: SelectProps = useMemo(
      () => ({
        options: paymentMethodOptions,
        isAllowClear: true,
        size: 'small',
      }),
      [paymentMethodOptions]
    );

    // Dummy multi-select props (no API)
    const dummyMultiSelectProps: SelectProps = useMemo(
      () => ({
        options: [],
        mode: 'multiple' as const,
        maxTagCount: 1,
        isAllowClear: true,
        size: 'small',
      }),
      []
    );

    const counterpartySelectProps: SelectProps = useMemo(
      () => ({
        ...dummyMultiSelectProps,
        placeholder: selectProps.counterparty.placeholder,
      }),
      [dummyMultiSelectProps, selectProps.counterparty.placeholder]
    );

    // ApPayee select props
    const apPayeeSelectProps = useMemo(
      () => ({
        mode: 'multiple' as const,
        maxTagCount: 1,
        className: 'apPayee',
        popupClassName: cx('admin-invoice-reports-ap-payee-dropdown'),
        inlineOptions: true,
        ...selectProps.apPayee,
        dropdownStyle: { maxHeight: 400, overflowY: 'auto' as const },
      }),
      [selectProps.apPayee]
    );

    const apPayeeCaptionField = useMemo(
      (): ['payment_method_text', 'account_display_name'] => [
        'payment_method_text',
        'account_display_name',
      ],
      []
    );

    // Row 1 spans: no dept (9+3+6+6=24), one dept (4+4+4+4+8=24), both depts (6+2+4+4+4+4=24)
    const hasBothDepts = deptsExisted?.enabled && reportDeptsSetting?.enabled;
    const hasOneDept = deptsExisted?.enabled && !reportDeptsSetting?.enabled;
    const keywordSpan = hasBothDepts ? 6 : hasOneDept ? 4 : 9;
    const numberSpan = hasBothDepts ? 2 : hasOneDept ? 4 : 3;
    const statusSpan = hasBothDepts ? 4 : hasOneDept ? 4 : 6;
    const applicantSpan = hasBothDepts ? 4 : hasOneDept ? 4 : 6;
    const deptSpan = hasBothDepts ? 4 : 8;

    return (
      <Form<AdminInvoiceReportsSearchFormData>
        formMethod={form}
        onSubmit={onSubmit}
      >
        {/* Row 1: キーワード, 申請番号, 申請ステータス, 申請者 [+ dept fields] — always sums to 24 */}
        <Row gutter={ROW_GUTTER} className={cx('search-grid')}>
          <Col span={keywordSpan}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <Label
                  text={labels.query.text}
                  tooltip={labels.query.tooltip}
                />
                <FormInput<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='query'
                  inputId='query'
                  testId='query-input'
                  inputProps={inputProps.query}
                />
              </div>
            </div>
          </Col>

          <Col span={numberSpan}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <KeywordCont
                  control={control}
                  name='number'
                  labelProps={labels.number}
                  inputProps={inputProps.number}
                />
              </div>
            </div>
          </Col>

          <Col span={statusSpan}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.status.text}
                  </label>
                  <FormSelect<AdminInvoiceReportsSearchFormData>
                    control={control}
                    name='status'
                    inputId='status'
                    testId='status'
                    selectProps={statusSelectProps}
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={applicantSpan}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.applicant.text}
                  </label>
                  <OfficeMember<AdminInvoiceReportsSearchFormData>
                    control={control}
                    name='applicant_ids'
                    inputId='applicant_ids'
                    testId='applicant-ids'
                    defaultOptions={memberOptions}
                    selectProps={selectProps.applicant}
                  />
                </div>
              </div>
            </div>
          </Col>

          {deptsExisted?.enabled && (
            <Col span={deptSpan}>
              <div className={cx('form-column')}>
                <div className={cx('form-field')}>
                  <div className={cx('select-wrapper')}>
                    <label className={cx('label-text')}>
                      {labels.applicant_department.text}
                    </label>
                    <DeptSelect<AdminInvoiceReportsSearchFormData>
                      mode='multiple'
                      control={control}
                      name='applicant_department_ids'
                      inputId='applicant_department_ids'
                      testId='applicant-department-ids'
                      defaultOptions={applicantDeptOptions}
                      selectProps={selectProps.applicantDepartment}
                    />
                  </div>
                </div>
              </div>
            </Col>
          )}

          {deptsExisted?.enabled && reportDeptsSetting?.enabled && (
            <Col span={4}>
              <div className={cx('form-column')}>
                <div className={cx('form-field')}>
                  <div className={cx('select-wrapper')}>
                    <label className={cx('label-text')}>
                      {labels.department.text}
                    </label>
                    <DeptSelect<AdminInvoiceReportsSearchFormData>
                      mode='multiple'
                      control={control}
                      name='department_ids'
                      inputId='department_ids'
                      testId='department-ids'
                      defaultOptions={reportDeptOptions}
                      selectProps={selectProps.department}
                    />
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>

        {/* Rows 2–3: 取引先, 支払先, 支払方法, 合計金額, 支払状況, 添付請求書, 作成日, 申請日, 承認日, 費用計上日, 支払期日, 支払日
            At default (span=6): 4 cols → 3 visual rows
            At xxl ≥1600px (xxl=4): 6 cols → 2 visual rows */}
        <Row gutter={ROW_GUTTER} className={cx('search-grid')}>
          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.counterparty.text}
                  </label>
                  <FormSelect<AdminInvoiceReportsSearchFormData>
                    control={control}
                    name='counterparty_ids'
                    inputId='counterparty_ids'
                    testId='counterparty-ids'
                    selectProps={counterpartySelectProps}
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper-ap-payee')}>
                  <label className={cx('label-text')}>
                    {labels.ap_payee.text}
                  </label>
                  <ApPayee
                    control={control}
                    name='ap_payee_ids'
                    inputId='ap_payee_ids'
                    formContext={undefined}
                    defaultOptions={EMPTY_AP_PAYEE_OPTIONS}
                    selectProps={apPayeeSelectProps}
                    include_inactive={false}
                    useDisplayName
                    hideInstantPayeeOnSearch
                    captionField={apPayeeCaptionField}
                    multipleCaptionLine
                    showCode
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.payment_method.text}
                  </label>
                  <FormSelect<AdminInvoiceReportsSearchFormData>
                    control={control}
                    name='payment_method'
                    inputId='payment_method'
                    testId='payment-method'
                    selectProps={paymentMethodSelectProps}
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('total-amount-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.total_amount.text}
                  </label>
                  <div className={cx('input-wrapper-number')}>
                    <FormInputNumber<AdminInvoiceReportsSearchFormData>
                      control={control}
                      name='total_amount_from'
                      inputId='total-amount-from'
                      inputProps={amountInputProps}
                      testId='total-amount-from'
                    />
                    <span className={cx('amount-range-separator')}>~</span>
                    <FormInputNumber<AdminInvoiceReportsSearchFormData>
                      control={control}
                      name='total_amount_to'
                      inputId='total-amount-to'
                      inputProps={amountInputProps}
                      testId='total-amount-to'
                    />
                  </div>
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.payment_status.text}
                  </label>
                  <PaymentStatusSelect
                    options={paymentStatusOptions}
                    control={control}
                    name='payment_status'
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <div className={cx('select-wrapper')}>
                  <label className={cx('label-text')}>
                    {labels.mf_file.text}
                  </label>
                  <FormSelect<AdminInvoiceReportsSearchFormData>
                    control={control}
                    name='mf_file'
                    inputId='mf_file'
                    testId='mf-file'
                    selectProps={mfFileSelectProps}
                  />
                </div>
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='created_date'
                  inputId='created_date'
                  datepickerProps={datepickerProps}
                  label={labels.created_date}
                  testId='created-date'
                />
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='submitted_date'
                  inputId='submitted_date'
                  datepickerProps={datepickerProps}
                  label={labels.submitted_date}
                  testId='submitted-date'
                />
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='approved_date'
                  inputId='approved_date'
                  datepickerProps={datepickerProps}
                  label={labels.approved_date}
                  testId='approved-date'
                />
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='book_date'
                  inputId='book_date'
                  datepickerProps={datepickerProps}
                  label={labels.book_date}
                  testId='book-date'
                />
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='due_date'
                  inputId='due_date'
                  datepickerProps={datepickerProps}
                  label={labels.due_date}
                  testId='due-date'
                />
              </div>
            </div>
          </Col>

          <Col span={6} xxl={4}>
            <div className={cx('form-column')}>
              <div className={cx('form-field')}>
                <FormDatepicker<AdminInvoiceReportsSearchFormData>
                  control={control}
                  name='payment_date'
                  inputId='payment_date'
                  datepickerProps={datepickerProps}
                  label={labels.payment_date}
                  testId='payment-date'
                />
              </div>
            </div>
          </Col>
        </Row>

        {/* Action Buttons */}
        <div className={cx('action-buttons')}>
          <ButtonV2
            size='sm'
            isTertiary
            onClick={onReset}
            testId='reset-button'
          >
            {labels.reset_button}
          </ButtonV2>
          <ButtonV2
            size='sm'
            isSecondary
            type='submit'
            buttonContainerClassName={cx('search-button-container')}
            isLoading={isLoading}
            isDisabled={isLoading}
            testId='search-button'
          >
            {labels.search_button}
          </ButtonV2>
        </div>
      </Form>
    );
  }
);

SearchForm.displayName = 'SearchForm';
